import React, { createContext, useCallback, useContext } from "react";
import {
  trackBottomNavClick,
  trackButtonClick,
  trackChatInteraction,
  trackEvent,
  trackFavoriteToggle,
  trackNavigation,
  trackRaceFilterClick,
  trackRaceNavigation,
  trackTipHistoryToggle,
} from "@/utils/analytics";
import logger from "@/utils/logger";

// Create context
const TrackingContext = createContext({
  trackButtonClick: () => {},
  trackRaceFilterClick: () => {},
  trackNavigation: () => {},
  trackRaceNavigation: () => {},
  trackTipHistoryToggle: () => {},
  trackFavoriteToggle: () => {},
  trackChatInteraction: () => {},
  trackBottomNavClick: () => {},
  trackEvent: () => {},
});

export const TrackingProvider = ({ children }) => {
  // Wrap each tracking function in useCallback to prevent unnecessary rerenders
  const handleButtonClick = useCallback((buttonName, additionalData = {}) => {
    // logger.log("TrackingProvider: handleButtonClick", {
    //   buttonName,
    //   additionalData,
    // });
    return trackButtonClick(buttonName, additionalData);
  }, []);

  const handleRaceFilterClick = useCallback(
    (trackName, raceNumber, isNextToJump = false, isInProgress = false) => {
      // logger.log("TrackingProvider: handleRaceFilterClick", {
      //   trackName,
      //   raceNumber,
      //   isNextToJump,
      //   isInProgress,
      // });
      return trackRaceFilterClick(
        trackName,
        raceNumber,
        isNextToJump,
        isInProgress,
      );
    },
    [],
  );

  const handleNavigation = useCallback((url, source = "") => {
    // logger.log("TrackingProvider: handleNavigation", { url, source });
    return trackNavigation(url, source);
  }, []);

  const handleRaceNavigation = useCallback(
    (trackName, raceNumber, source = "") => {
      // logger.log("TrackingProvider: handleRaceNavigation", {
      //   trackName,
      //   raceNumber,
      //   source,
      // });
      return trackRaceNavigation(trackName, raceNumber, source);
    },
    [],
  );

  const handleTipHistoryToggle = useCallback((isExpanded) => {
    // logger.log("TrackingProvider: handleTipHistoryToggle", { isExpanded });
    return trackTipHistoryToggle(isExpanded);
  }, []);

  const handleFavoriteToggle = useCallback((horseId, horseName, action) => {
    // logger.log("TrackingProvider: handleFavoriteToggle", {
    //   horseId,
    //   horseName,
    //   action,
    // });
    return trackFavoriteToggle(horseId, horseName, action);
  }, []);

  const handleChatInteraction = useCallback((action, additionalData = {}) => {
    // logger.log("TrackingProvider: handleChatInteraction", {
    //   action,
    //   additionalData,
    // });
    return trackChatInteraction(action, additionalData);
  }, []);

  const handleBottomNavClick = useCallback((tabName) => {
    // logger.log("TrackingProvider: handleBottomNavClick", { tabName });
    return trackBottomNavClick(tabName);
  }, []);

  const handleEvent = useCallback((eventName, eventData = {}) => {
    // logger.log("TrackingProvider: handleEvent", { eventName, eventData });
    return trackEvent(eventName, eventData);
  }, []);

  // Create the context value
  const contextValue = {
    trackButtonClick: handleButtonClick,
    trackRaceFilterClick: handleRaceFilterClick,
    trackNavigation: handleNavigation,
    trackRaceNavigation: handleRaceNavigation,
    trackTipHistoryToggle: handleTipHistoryToggle,
    trackFavoriteToggle: handleFavoriteToggle,
    trackChatInteraction: handleChatInteraction,
    trackBottomNavClick: handleBottomNavClick,
    trackEvent: handleEvent,
  };

  return (
    <TrackingContext.Provider value={contextValue}>
      {children}
    </TrackingContext.Provider>
  );
};

// Custom hook to use the tracking context
export const useTracking = () => {
  const context = useContext(TrackingContext);

  if (context === undefined) {
    throw new Error("useTracking must be used within a TrackingProvider");
  }

  return context;
};

// HOC to inject tracking props into components
export const withTracking = (Component) => {
  const WithTracking = (props) => {
    const tracking = useTracking();
    return <Component {...props} tracking={tracking} />;
  };

  WithTracking.displayName = `WithTracking(${Component.displayName || Component.name || "Component"})`;

  return WithTracking;
};

// Utility component for tracking clicks
export const TrackingButton = ({
  onClick,
  trackingName,
  trackingData = {},
  children,
  ...props
}) => {
  const { trackButtonClick } = useTracking();

  const handleClick = (e) => {
    trackButtonClick(trackingName, trackingData);
    if (onClick) onClick(e);
  };

  return (
    <button {...props} onClick={handleClick}>
      {children}
    </button>
  );
};

export default TrackingProvider;
